/**
 * Formatting utility functions
 * 
 * This file contains utility functions for formatting provider data
 * for display in the UI. Handles rating, distance, service ranges,
 * reasoning text, and service area parsing.
 */

import type { ApiProviderProfile } from '../types/matchmaking.types';

/**
 * Formats provider rating as a rounded integer string
 * 
 * @param providerProfile - Optional provider profile containing rating
 * @returns Formatted rating string or null if no rating available
 */
export const formatRating = (providerProfile?: ApiProviderProfile): string | null => {
  if (providerProfile?.rating) {
    return Math.round(providerProfile.rating).toString();
  }
  return null;
};

/**
 * Formats distance in miles as a rounded integer string
 * 
 * Handles both string and number distance values, parsing strings
 * to numbers before formatting.
 * 
 * @param providerProfile - Optional provider profile containing distanceMiles
 * @returns Formatted distance string or null if no distance available
 */
export const formatDistance = (providerProfile?: ApiProviderProfile): string | null => {
  const rawDistance = providerProfile?.distanceMiles;
  const distanceNumber = typeof rawDistance === 'string' ? parseFloat(rawDistance) : rawDistance;
  if (distanceNumber !== undefined && distanceNumber !== null && !Number.isNaN(distanceNumber)) {
    return Math.round(distanceNumber).toString();
  }
  return null;
};

/**
 * Extracts and validates service ranges from provider profile
 * 
 * Filters out invalid values (empty, null, undefined, "N/A", etc.)
 * Falls back to serviceType if provider profile doesn't have service_ranges.
 * 
 * @param providerProfile - Optional provider profile with service_ranges array
 * @param serviceType - Optional fallback service type string
 * @returns Array of valid service range strings
 */
export const getServiceRanges = (
  providerProfile?: ApiProviderProfile,
  serviceType?: string,
): string[] => {
  const isValidRange = (range: string) => {
    if (!range || typeof range !== 'string') return false;
    const trimmed = range.trim();
    const lower = trimmed.toLowerCase();
    if (trimmed === '' || trimmed === '-' || lower === 'n/a' || lower === 'null' || lower === 'undefined') {
      return false;
    }
    return true;
  };

  if (providerProfile?.service_ranges && providerProfile.service_ranges.length > 0) {
    return providerProfile.service_ranges.filter(isValidRange);
  }
  if (serviceType && isValidRange(serviceType)) {
    return [serviceType];
  }
  return [];
};

/**
 * Gets the reasoning/description text for a provider
 * 
 * Priority order:
 * 1. AI rationale (if available)
 * 2. Semantic summary (if available)
 * 3. Company overview (if available)
 * 4. Default fallback message
 * 
 * @param aiRationale - Optional AI-generated rationale text
 * @param providerProfile - Optional provider profile with summary/overview
 * @returns Reasoning text string for display
 */
export const getReasoningText = (
  aiRationale?: string,
  providerProfile?: ApiProviderProfile,
): string => {
  if (aiRationale?.trim()) {
    return aiRationale;
  }
  if (providerProfile?.semantic_summary) {
    return providerProfile.semantic_summary;
  }
  if (providerProfile?.companyOverview) {
    return providerProfile.companyOverview;
  }
  return "Provides compassionate, personalized care across key specialties, ensuring safety, comfort, and consistent support.";
};

/**
 * Parses service area string to extract counties and format for display
 * 
 * Extracts county names from parentheses in format "State (County1, County2, ...)"
 * Returns visible counties (first 3-4), remaining counties, and display text.
 * 
 * @param serviceArea - Service area string with state and counties
 * @returns Object with displayText, counties array, remainingCounties, and hasMore flag
 */
export const parseServiceArea = (serviceArea: string) => {
  if (serviceArea === "-" || serviceArea === "N/A" || !serviceArea) {
    return {
      displayText: "-",
      counties: [],
      remainingCounties: [],
      hasMore: false,
    };
  }

  const counties: string[] = [];
  const stateCountyRegex = /\(([^)]+)\)/g;
  let match;
  
  while ((match = stateCountyRegex.exec(serviceArea)) !== null) {
    const countyList = match[1];
    const countyArray = countyList.split(',').map(c => c.trim());
    counties.push(...countyArray);
  }

  if (counties.length === 0) {
    return {
      displayText: serviceArea,
      counties: [],
      remainingCounties: [],
      hasMore: false,
    };
  }

  const maxVisible = counties.length > 7 ? 3 : 4;
  const visibleCounties = counties.slice(0, maxVisible);
  const remainingCounties = counties.slice(maxVisible);
  const hasMore = remainingCounties.length > 0;

  const displayText = visibleCounties.join(', ');

  return {
    displayText,
    counties: visibleCounties,
    remainingCounties,
    hasMore,
    totalCount: counties.length,
  };
};

